import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { pendingCount, localReportsCount, syncPending } from "../lib/offline";
import { WifiOff, RefreshCw, Loader2, CloudUpload } from "lucide-react";

export function OfflineBar() {
  const { t } = useLang();
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [count, setCount] = useState(0);
  const [reports, setReports] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(() => { pendingCount().then(setCount); localReportsCount().then(setReports); }, []);

  const sync = useCallback(async (silent = false) => {
    if (!navigator.onLine) {
      if (!silent) toast.error(t("Toujours hors ligne. Les saisies restent en attente."));
      return;
    }
    setSyncing(true);
    try {
      const res = await syncPending(api);
      if (res.sent > 0) toast.success(`${res.sent} ${t("saisie(s) synchronisée(s) avec le serveur.")}`);
      if (res.reports > 0) toast.success(`${res.reports} ${t("rapport(s) généré(s) hors ligne envoyé(s) au serveur.")}`);
      else if (!silent && res.total === 0 && res.sent === 0) toast.success(t("Aucune saisie en attente."));
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [t, refresh]);

  useEffect(() => {
    refresh();
    const on = () => { setOffline(false); sync(true); };
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("stride-queue-changed", refresh);
    if (navigator.onLine) sync(true);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("stride-queue-changed", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!offline && count === 0 && reports === 0) return null;
  const any = count > 0 || reports > 0;

  return (
    <div data-testid="offline-bar"
      className={`flex flex-wrap items-center gap-2 px-4 py-2 text-xs font-medium border-b ${
        offline ? "bg-[#0F172A] text-white border-slate-900" : "bg-amber-50 text-amber-900 border-amber-200"}`}>
      {offline ? <WifiOff className="h-3.5 w-3.5" /> : <CloudUpload className="h-3.5 w-3.5" />}
      <span>{offline ? t("Hors ligne · vos saisies sont enregistrées sur l'appareil") : t("Connexion rétablie")}</span>
      {count > 0 && (
        <span data-testid="offline-pending" className={`px-2 py-0.5 rounded-full font-semibold ${offline ? "bg-white/15" : "bg-amber-200/70"}`}>
          {count} {t("saisie(s) en attente")}
        </span>
      )}
      {reports > 0 && (
        <span data-testid="offline-pending-reports" className={`px-2 py-0.5 rounded-full font-semibold ${offline ? "bg-white/15" : "bg-amber-200/70"}`}>
          {reports} {t("rapport(s) à envoyer")}
        </span>
      )}
      {any && (
        <button onClick={() => sync(false)} disabled={syncing} data-testid="offline-sync-btn"
          className={`inline-flex items-center gap-1 ml-auto px-2.5 py-1 rounded-md font-semibold transition-colors ${
            offline ? "bg-white/10 hover:bg-white/20" : "bg-amber-900 text-white hover:bg-amber-800"}`}>
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {t("Synchroniser maintenant")}
        </button>
      )}
    </div>
  );
}

export default OfflineBar;
